/**
 * Zod schemas — catalogue paramétrable (formats × framings × supports).
 *
 * Validation client avant envoi BE. Le BE reste source de vérité (Pydantic) ;
 * tout mismatch est attrapé par codegen OpenAPI (`gen:api:check`).
 *
 * `active` n'est PAS dans les form schemas — le toggle activer/désactiver
 * passera par une mutation dédiée (S2.5b ou plus tard) et non par les modals
 * create/edit.
 */

import { z } from 'zod'

const FORMAT_KIND = z.enum(['print', 'original', 'custom', 'digital'])

// `optionalString` : accepte une chaîne vide depuis l'UI et la transforme en
// undefined. Évite que ` ` ou `''` soit envoyé comme valeur du champ optionnel.
const optionalString = (max: number) =>
  z
    .string()
    .max(max)
    .optional()
    .transform((v) => (v === undefined || v === '' ? undefined : v))

// ──────────────────────────────────────────────────────────────────────────────
// Formats — libellé dérivé service, kinds multi-valué (S2.5e)
// ──────────────────────────────────────────────────────────────────────────────
//
// Le libellé (`label_fr`/`label_en`) est dérivé pure côté BE depuis
// (width_cm, height_cm) — pas de champ saisissable. Un format peut porter
// plusieurs `kinds` (≥1) : ex. `print` + `digital` pour vendre tirage papier
// et fichier digital sous les mêmes dimensions.

export const formatFormSchema = z.object({
  width_cm: z.coerce.number().gt(0, 'Largeur requise').max(9999.99, 'Maximum 9999.99 cm'),
  height_cm: z.coerce.number().gt(0, 'Hauteur requise').max(9999.99, 'Maximum 9999.99 cm'),
  kinds: z.array(FORMAT_KIND).min(1, 'Sélectionne au moins un type'),
  display_order: z.coerce.number().int().min(0),
  // Refonte POD v6.1 : `prodigi_sku` retiré du domaine A (lexique
  // artistique). Le SKU Gelato vit désormais dans `printable_specs.sku`
  // (domaine C), lié à la variante via `work_variants.printable_spec_id`.
})

export type FormatFormValues = z.input<typeof formatFormSchema>
export type FormatFormParsed = z.output<typeof formatFormSchema>

// ──────────────────────────────────────────────────────────────────────────────
// Framings
// ──────────────────────────────────────────────────────────────────────────────

const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/

export const framingFormSchema = z.object({
  label_fr: z.string().min(1, 'Libellé requis').max(120),
  label_en: optionalString(120),
  color_hex: z
    .string()
    .optional()
    .transform((v) => (v === undefined || v === '' ? undefined : v))
    .refine((v) => v === undefined || HEX_PATTERN.test(v), 'Format #RRGGBB attendu'),
  material: optionalString(120),
  surcharge_cents: z.coerce.number().int().min(0),
  display_order: z.coerce.number().int().min(0),
  // Refonte POD v6.1 : `prodigi_frame` retiré du domaine A. L'attribut
  // `frame` Gelato vit dans `printable_specs.attributes` (domaine C).
})

export type FramingFormValues = z.input<typeof framingFormSchema>
export type FramingFormParsed = z.output<typeof framingFormSchema>

// ──────────────────────────────────────────────────────────────────────────────
// Supports
// ──────────────────────────────────────────────────────────────────────────────
//
// Refonte POD v6.1 : `prodigi_paper` + `pod_category` retirés. Le papier
// Gelato vit dans `printable_specs.attributes.paper` (domaine C).

export const supportFormSchema = z.object({
  label_fr: z.string().min(1, 'Libellé requis').max(120),
  label_en: optionalString(120),
  finish: optionalString(80),
  display_order: z.coerce.number().int().min(0),
})

export type SupportFormValues = z.input<typeof supportFormSchema>
export type SupportFormParsed = z.output<typeof supportFormSchema>

// ──────────────────────────────────────────────────────────────────────────────
// Emotional intents + Target audiences (Tier 2 — slug ANGLAIS validé Claude)
// ──────────────────────────────────────────────────────────────────────────────
//
// Le slug ne fait PAS partie du formulaire UI principal — il est obtenu
// dans une sous-modal de validation Claude. Le schéma de saisie initiale
// ne contient que `label_fr` + `display_order`. Le slug confirmé est
// ajouté juste avant le POST.

export const tier2LabelFormSchema = z.object({
  label_fr: z.string().min(1, 'Libellé requis').max(120),
  display_order: z.coerce.number().int().min(0),
})

export type Tier2LabelFormValues = z.input<typeof tier2LabelFormSchema>
export type Tier2LabelFormParsed = z.output<typeof tier2LabelFormSchema>

// Schéma du slug confirmé dans la sous-modal de validation Claude.
const SLUG_PATTERN = /^[a-z0-9_-]{1,80}$/

export const slugConfirmSchema = z.object({
  slug: z
    .string()
    .min(1, 'Slug requis')
    .max(80)
    .regex(SLUG_PATTERN, 'Lettres minuscules, chiffres, `-` ou `_` uniquement'),
})

export type SlugConfirmFormValues = z.input<typeof slugConfirmSchema>
export type SlugConfirmFormParsed = z.output<typeof slugConfirmSchema>
